import { Fragment, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { hasDetachedCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useAuth } from "@/context/AuthContext";
import { canOperateInventory } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { fmtDate, statusLabel, variantLabel } from "@/lib/format";
import * as returnsApi from "@/lib/api/returns";
import {
  clampReturnQty,
  RETURN_PHYSICAL_NOTICE,
  returnEligibleQty,
  suggestedReceivingLocationId,
} from "@/lib/reverse-lifecycle";
import { downloadCsv, toCsv } from "@/lib/csv";
import Pagination from "@/components/base/Pagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

interface ReturnRow {
  id: string;
  return_number: string;
  customer_id: string | null;
  location_id: string | null;
  sales_order_id: string | null;
  status: string;
  reason: string | null;
  customers?: { name: string } | null;
  locations?: { name: string } | null;
  sales_orders?: { order_number: string } | null;
  created_at: string;
}

interface ReturnItem extends CatalogueHistoryFields {
  id: string;
  sales_return_id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_returned: number;
  condition: string;
  restocked: boolean;
  product_name: string | null;
  sku: string | null;
  variant_options: Record<string, string> | null;
  products?: { name: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface Option { id: string; name: string; }
interface ProductOption { id: string; name: string; sku: string; }
interface SoOption { id: string; order_number: string; customer_id: string | null; }
interface SoItemRow extends CatalogueHistoryFields {
  id: string;
  sales_order_id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_fulfilled: number;
  sku: string | null;
  product_name: string | null;
  variant_attributes: Record<string, string> | null;
}
interface PriorReturnQty { sales_order_item_id: string | null; qty_returned: number; status: string | null }
interface FulfilmentLoc { sales_order_id: string; location_id: string | null }
interface DraftLine { uid: string; product_id: string; variant_id: string | null; qty: number; condition: string; sales_order_item_id: string | null; }
interface VariantOption { id: string; product_id: string; sku: string | null; option_values: Record<string, string> | null; }

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

const conditionLabel: Record<string, string> = {
  resellable: "Resellable",
  defective: "Defective",
  damaged: "Damaged",
};

export default function Returns() {
  const { org, role } = useAuth();
  const canCreate = canOperateInventory(role);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const presetOrderId = searchParams.get("orderId") ?? "";
  const presetConsumed = useRef(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [salesOrderId, setSalesOrderId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [createIdemKey, setCreateIdemKey] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["returns", org?.id, debouncedSearch, statusFilter, page, pageSize],
    enabled: !!org?.id,
    queryFn: async () => {
      let q = supabase
        .from("sales_returns")
        .select("id, return_number, customer_id, location_id, sales_order_id, status, reason, created_at, customers(name), locations(name), sales_orders!sales_order_id(order_number)", { count: "exact" })
        .eq("org_id", org!.id);
      if (statusFilter) q = q.eq("status", statusFilter);
      const term = debouncedSearch.trim();
      if (term) {
        const ilike = `%${term}%`;
        const [custRes, orderRes] = await Promise.all([
          supabase.from("customers").select("id").eq("org_id", org!.id).ilike("name", ilike).limit(100),
          supabase.from("sales_orders").select("id").eq("org_id", org!.id).ilike("order_number", ilike).limit(100),
        ]);
        const custIds = (custRes.data ?? []).map((c: { id: string }) => c.id);
        const orderIds = (orderRes.data ?? []).map((o: { id: string }) => o.id);
        const parts = [`return_number.ilike.${ilike}`];
        if (custIds.length) parts.push(`customer_id.in.(${custIds.join(",")})`);
        if (orderIds.length) parts.push(`sales_order_id.in.(${orderIds.join(",")})`);
        q = q.or(parts.join(","));
      }
      q = q.order("created_at", { ascending: false }).range(page * pageSize, page * pageSize + pageSize - 1);
      const returnsRes = await q;
      const returns = (returnsRes.data ?? []).map((row) => ({
        ...row,
        customers: firstRelation(row.customers),
        locations: firstRelation(row.locations),
        sales_orders: firstRelation(row.sales_orders),
      })) as ReturnRow[];
      const ids = returns.map((r) => r.id);
      const [items, customers, locations, products, sos, soItems, variants, priorReturns, fulfilments] = await Promise.all([
        ids.length ? supabase.from("sales_return_items").select("id, sales_return_id, product_id, variant_id, qty_returned, condition, restocked, product_name, sku, variant_options, catalogue_product_identity_id, catalogue_variant_identity_id, products(name), variants(sku, option_values)").eq("org_id", org!.id).in("sales_return_id", ids) : Promise.resolve({ data: [] }),
        supabase.from("customers").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("products").select("id, name, sku").eq("org_id", org!.id).neq("status", "archived").order("name"),
        supabase.from("sales_orders").select("id, order_number, customer_id").eq("org_id", org!.id).in("status", ["fulfilled", "partially_fulfilled", "completed", "returned", "partially_returned"]).order("created_at", { ascending: false }),
        supabase.from("sales_order_items").select("id, sales_order_id, product_id, variant_id, qty_fulfilled, sku, product_name, variant_attributes, catalogue_product_identity_id, catalogue_variant_identity_id").eq("org_id", org!.id).gt("qty_fulfilled", 0),
        supabase.from("variants").select("id, product_id, sku, option_values").eq("org_id", org!.id).neq("status", "archived").order("position"),
        supabase.from("sales_return_items").select("sales_order_item_id, qty_returned, sales_returns(status)").eq("org_id", org!.id),
        supabase.from("fulfilments").select("sales_order_id, location_id").eq("org_id", org!.id).in("status", ["fulfilled", "shipped", "completed"]),
      ]);
      const priorReturnRows = ((priorReturns.data ?? []) as Array<{
        sales_order_item_id: string | null;
        qty_returned: number;
        sales_returns: { status: string } | { status: string }[] | null;
      }>).map((row) => ({
        sales_order_item_id: row.sales_order_item_id,
        qty_returned: row.qty_returned,
        status: firstRelation(row.sales_returns)?.status ?? null,
      })) as PriorReturnQty[];
      return {
        returns,
        total: returnsRes.count ?? 0,
        items: (items.data ?? []) as ReturnItem[],
        customers: (customers.data ?? []) as Option[],
        locations: (locations.data ?? []) as Option[],
        products: (products.data ?? []) as ProductOption[],
        sos: (sos.data ?? []) as SoOption[],
        soItems: (soItems.data ?? []) as SoItemRow[],
        variants: (variants.data ?? []) as VariantOption[],
        priorReturnRows,
        fulfilments: (fulfilments.data ?? []) as FulfilmentLoc[],
      };
    },
  });

  const itemsByReturn = (data?.items ?? []).reduce<Record<string, ReturnItem[]>>((acc, it) => {
    (acc[it.sales_return_id] = acc[it.sales_return_id] ?? []).push(it);
    return acc;
  }, {});

  const statusOptions = ["draft", "approved", "received", "completed", "cancelled"];

  const addLine = () => setLines((l) => [...l, { uid: crypto.randomUUID(), product_id: "", variant_id: null, qty: 1, condition: "resellable", sales_order_item_id: null }]);
  const updateLine = (idx: number, patch: Partial<DraftLine>) =>
    setLines((l) => l.map((line, i) => (i === idx ? { ...line, ...patch } : line)));
  const removeLine = (idx: number) => setLines((l) => l.filter((_, i) => i !== idx));

  const alreadyReturnedForItem = (salesOrderItemId: string | null) => {
    if (!salesOrderItemId) return 0;
    return (data?.priorReturnRows ?? [])
      .filter((row) => row.sales_order_item_id === salesOrderItemId && row.status !== "cancelled")
      .reduce((sum, row) => sum + Number(row.qty_returned || 0), 0);
  };

  const lineFacts = (line: DraftLine) => {
    const product = (data?.products ?? []).find((p) => p.id === line.product_id);
    const variant = (data?.variants ?? []).find((v) => v.id === line.variant_id);
    const soItem = (data?.soItems ?? []).find((it) => it.id === line.sales_order_item_id);
    const fulfilled = soItem?.qty_fulfilled ?? 0;
    const already = alreadyReturnedForItem(line.sales_order_item_id);
    const maxReturnable = soItem ? returnEligibleQty(fulfilled, 0, already) : 0;
    return {
      productName: soItem?.product_name || product?.name || "Historical product",
      retired: soItem ? hasDetachedCatalogueIdentity(soItem) : false,
      options: variantLabel(soItem?.variant_attributes ?? variant?.option_values) || "—",
      sku: soItem?.sku || variant?.sku || product?.sku || "—",
      fulfilled,
      already,
      maxReturnable,
    };
  };

  const handleSelectOrder = useCallback((soId: string) => {
    setSalesOrderId(soId);
    if (!soId) return;
    const so = data?.sos.find((s) => s.id === soId);
    if (so?.customer_id) setCustomerId(so.customer_id);
    const orgLocationIds = (data?.locations ?? []).map((l) => l.id);
    const fulfilment = (data?.fulfilments ?? []).find((f) => f.sales_order_id === soId);
    setLocationId(suggestedReceivingLocationId({
      fulfilmentLocationId: fulfilment?.location_id,
      orgLocationIds,
    }));
    const fulfilled = (data?.soItems ?? []).filter((it) => it.sales_order_id === soId);
    if (fulfilled.length > 0) {
      setLines(fulfilled.map((it) => {
        const already = (data?.priorReturnRows ?? [])
          .filter((row) => row.sales_order_item_id === it.id && row.status !== "cancelled")
          .reduce((sum, row) => sum + Number(row.qty_returned || 0), 0);
        const maxReturnable = returnEligibleQty(it.qty_fulfilled, 0, already);
        return {
          uid: crypto.randomUUID(),
          product_id: it.product_id ?? "",
          variant_id: it.variant_id,
          qty: clampReturnQty(maxReturnable, maxReturnable),
          condition: "resellable",
          sales_order_item_id: it.id,
        };
      }));
    }
  }, [data]);

  // Deep-link support: /returns?orderId=<id> opens the create modal with the
  // order preselected (used by the sales-order detail "New return" action).
  useEffect(() => {
    if (presetConsumed.current || !presetOrderId || !data) return;
    const so = data.sos.find((s) => s.id === presetOrderId);
    presetConsumed.current = true;
    if (!canCreate || !so) return;
    setError("");
    setCreateIdemKey(crypto.randomUUID());
    handleSelectOrder(presetOrderId);
    setOpen(true);
  }, [presetOrderId, data, canCreate, handleSelectOrder]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["returns", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleExport = () => {
    const headers = ["return_number", "customer", "order", "location", "reason", "status", "created_at"];
    const rows = (data?.returns ?? []).map((r) => [
      r.return_number,
      r.customers?.name ?? "",
      r.sales_orders?.order_number ?? "",
      r.locations?.name ?? "",
      r.reason ?? "",
      r.status,
      fmtDate(r.created_at),
    ]);
    downloadCsv(`returns-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, rows));
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!customerId || !locationId || lines.length === 0) {
      setError("Select a customer, receiving location, and at least one item.");
      return;
    }
    if (lines.some((l) => (!l.product_id && !l.sales_order_item_id) || Number(l.qty) <= 0)) {
      setError("Every line needs a product or a source order line and a positive quantity.");
      return;
    }
    if (lines.some((l) => {
      const facts = lineFacts(l);
      return Number(l.qty) > facts.maxReturnable;
    })) {
      setError("Qty to return cannot exceed the maximum returnable quantity.");
      return;
    }
    if (lines.some((l) => {
      const vs = (data?.variants ?? []).filter((v) => v.product_id === l.product_id);
      return !l.sales_order_item_id && vs.length > 1 && !l.variant_id;
    })) {
      setError("Select a variant for each variable product.");
      return;
    }
    setSaving(true);

    // One transactional call: the header and every return line commit or roll
    // back together, the number comes from a sequence, and each line is validated
    // against its fulfilled/eligible quantity server-side before anything writes.
    const pLines = lines.map((l) => ({
      product_id: l.product_id || null,
      variant_id: l.variant_id ?? null,
      qty: Number(l.qty),
      condition: l.condition,
      sales_order_item_id: l.sales_order_item_id ?? null,
    }));

    try {
      await returnsApi.createSalesReturn({
        lines: pLines,
        salesOrderId: salesOrderId || null,
        customerId,
        locationId,
        reason: reason || null,
        idempotencyKey: createIdemKey,
      });
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }

    setSaving(false);

    setOpen(false);
    setSalesOrderId("");
    setCustomerId("");
    setLocationId("");
    setReason("");
    setLines([]);
    invalidate();
  };

  if (isLoading) return <ListSkeleton />;

  return (
    <div>
      <PageHeader title="Returns" subtitle="Process customer and product returns">
        <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-2-line" /> Export
        </button>
        {canCreate && (
          <button onClick={() => { setError(""); setCreateIdemKey(crypto.randomUUID()); setOpen(true); }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" />
            New Return
          </button>
        )}
      </PageHeader>

      {error && <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load returns.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      ) : !data || data.total === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState title="No returns" description="Record a return when items come back from a customer." />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search by return, customer or order..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="">All statuses</option>
              {statusOptions.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
            <span className="inline-flex items-center text-xs text-slate-400 whitespace-nowrap">{data?.total ?? 0} returns</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Return</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reason</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.returns.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">No returns match your search or filter.</td>
                  </tr>
                )}
                {data.returns.map((r) => {
                  const items = itemsByReturn[r.id] ?? [];
                  const isOpen = expanded === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => setExpanded(isOpen ? null : r.id)}>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/returns/${r.id}`); }}
                            className="text-sm font-semibold text-slate-800 font-mono hover:text-emerald-700 whitespace-nowrap"
                          >
                            {r.return_number}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.customers?.name ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 font-mono">{r.sales_orders?.order_number ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.locations?.name ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.reason ?? "—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-slate-50 bg-slate-50/40">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="divide-y divide-slate-100">
                              {items.map((it) => (
                                <div key={it.id} className="py-2 flex items-center justify-between text-sm">
                                  <span className="font-medium text-slate-800">
                                    {it.product_name ?? it.products?.name ?? "—"}
                                    {(variantLabel(it.variant_options ?? it.variants?.option_values) || it.sku || it.variants?.sku) && (
                                      <span className="text-slate-500 font-normal ml-1">· {variantLabel(it.variant_options ?? it.variants?.option_values) || it.sku || it.variants?.sku}</span>
                                    )}
                                  </span>
                                  <div className="flex items-center gap-6 text-slate-600">
                                    <span>{conditionLabel[it.condition] ?? it.condition}</span>
                                    <span>{it.restocked ? "Restocked" : "Pending"}</span>
                                    <span className="w-10 text-right">{it.qty_returned}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Return" wide
        footer={
          <>
            <button onClick={() => setOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Creating..." : "Create Return"}
            </button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
            {RETURN_PHYSICAL_NOTICE}
          </div>
          <div>
            <label className={labelCls}>Original sales order (optional)</label>
            <select value={salesOrderId} onChange={(e) => handleSelectOrder(e.target.value)} className={inputCls}>
              <option value="">No linked order</option>
              {data?.sos.map((s) => <option key={s.id} value={s.id}>{s.order_number}</option>)}
            </select>
            <p className="text-xs text-slate-400 mt-1">Selecting an order auto-fills the customer, fulfilled items, and receiving location from that fulfilment when known.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Customer *</label>
              <select required value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={inputCls}>
                <option value="">Select customer...</option>
                {data?.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Receiving location *</label>
              <select required value={locationId} onChange={(e) => setLocationId(e.target.value)} className={inputCls}>
                <option value="">Select location...</option>
                {data?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Reason</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} placeholder="Defective, wrong item, etc." />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-500">Returned items</label>
              <button type="button" onClick={addLine} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                <i className="ri-add-line" /> Add item
              </button>
            </div>
            {lines.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">No items yet.</div>
            ) : (
              <div className="space-y-3">
                {lines.map((l, idx) => {
                  const lineVariants = (data?.variants ?? []).filter((v) => v.product_id === l.product_id);
                  const facts = lineFacts(l);
                  return (
                  <div key={l.uid} className="border border-slate-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <select value={l.product_id} onChange={(e) => { const vs = (data?.variants ?? []).filter((v) => v.product_id === e.target.value); updateLine(idx, { product_id: e.target.value, variant_id: vs.length === 1 ? vs[0].id : null }); }} className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                        <option value="">Select product...</option>
                        {data?.products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                      </select>
                      <button type="button" onClick={() => removeLine(idx)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                    {lineVariants.length === 1 ? (
                      <p className="text-xs text-slate-600">{variantLabel(lineVariants[0].option_values) || lineVariants[0].sku || "Default"}</p>
                    ) : lineVariants.length > 1 ? (
                      <select
                        value={l.variant_id ?? ""}
                        onChange={(e) => updateLine(idx, { variant_id: e.target.value || null })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                      >
                        <option value="">Select variant…</option>
                        {lineVariants.map((v) => <option key={v.id} value={v.id}>{variantLabel(v.option_values) || v.sku}</option>)}
                      </select>
                    ) : null}
                    <dl className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-slate-600">
                      <div><dt className="text-slate-400">Product</dt><dd className="font-medium text-slate-800">{facts.productName}</dd>{facts.retired && <p className="text-xs text-amber-700">Deleted catalogue identity. Return and refund remain available; do not restock into a replacement SKU.</p>}</div>
                      <div><dt className="text-slate-400">Variant / options</dt><dd>{facts.options}</dd></div>
                      <div><dt className="text-slate-400">SKU</dt><dd className="font-mono">{facts.sku}</dd></div>
                      <div><dt className="text-slate-400">Fulfilled quantity</dt><dd>{facts.fulfilled}</dd></div>
                      <div><dt className="text-slate-400">Already returned</dt><dd>{facts.already}</dd></div>
                      <div><dt className="text-slate-400">Maximum returnable</dt><dd>{facts.maxReturnable}</dd></div>
                    </dl>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-medium text-slate-500 whitespace-nowrap">Qty to return</label>
                      <input
                        type="number"
                        min={facts.maxReturnable > 0 ? 1 : 0}
                        max={facts.maxReturnable}
                        value={l.qty}
                        onChange={(e) => updateLine(idx, { qty: clampReturnQty(Number(e.target.value), facts.maxReturnable) })}
                        className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                      />
                      <select value={l.condition} onChange={(e) => updateLine(idx, { condition: e.target.value })} className="w-36 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                        <option value="resellable">Resellable</option>
                        <option value="defective">Defective</option>
                        <option value="damaged">Damaged</option>
                      </select>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
}
